import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

interface Column {
  id?: string;
  field?: string;
  label?: string;
  headerName?: string;
  minWidth?: number;
  width?: number;
  flex?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => any;
  renderCell?: (params: any) => any;
}

interface DataTableProps {
  columns: Column[];
  rows?: any[];
  data?: any[];
  loading?: boolean;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (id: number) => void;
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (newPage: number) => void;
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
  enhanced?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  data,
  loading = false,
  onView,
  onEdit,
  onDelete,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  enhanced = true,
}) => {
  const tableData = rows || data || [];
  
  const handleChangePage = (event: unknown, newPage: number) => {
    if (onPageChange) onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onRowsPerPageChange) onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const headerCellSx = enhanced
    ? {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        color: 'text.secondary',
        bgcolor: 'grey.50',
        borderBottom: '2px solid',
        borderColor: 'divider',
        whiteSpace: 'nowrap' as const,
      }
    : undefined;

  const actionButtonSx = enhanced
    ? {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        mx: 0.25,
      }
    : undefined;

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        border: enhanced ? '1px solid' : undefined,
        borderColor: enhanced ? 'divider' : undefined,
        borderRadius: enhanced ? 2 : undefined,
      }}
      elevation={enhanced ? 0 : 1}
    >
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size={enhanced ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {columns.map((column, idx) => (
                <TableCell
                  key={column.id || column.field || idx}
                  align={column.align}
                  style={{ minWidth: column.minWidth || column.width }}
                  sx={headerCellSx}
                >
                  {column.label || column.headerName}
                </TableCell>
              ))}
              {(onView || onEdit || onDelete) && (
                <TableCell align="right" sx={headerCellSx}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Box sx={{ p: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : tableData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Box sx={{ py: 4, color: 'text.secondary' }}>No data available</Box>
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row) => {
                const rowId = row.id || row.attributes?.id;
                const normalizedRow = row.attributes ? { ...row.attributes, id: row.id } : row;
                
                return (
                  <TableRow
                    hover
                    key={rowId}
                    sx={
                      enhanced
                        ? {
                            '&:last-child td': { borderBottom: 0 },
                            '&:hover': { bgcolor: 'action.hover' },
                          }
                        : undefined
                    }
                  >
                    {columns.map((column, idx) => {
                      const columnKey = column.id || column.field || '';
                      let rawValue = columnKey ? (normalizedRow[columnKey] ?? row[columnKey]) : '';
                      
                      if (column.renderCell) {
                        return (
                          <TableCell key={columnKey || idx} align={column.align}>
                            {column.renderCell({ value: rawValue, row: normalizedRow })}
                          </TableCell>
                        );
                      }
                      
                      if (column.format) {
                        return (
                          <TableCell key={columnKey || idx} align={column.align}>
                            {column.format(rawValue)}
                          </TableCell>
                        );
                      }
                      
                      let value = rawValue;
                      
                      if (value && typeof value === 'object' && value.data && !value.id && !value.name) {
                        value = value.data;
                      }
                      
                      if (value && typeof value === 'object' && value !== null) {
                        if (Array.isArray(value)) {
                          value = value.join(', ');
                        } else if (value.attributes) {
                          if (value.attributes.name !== undefined) {
                            value = value.attributes.name;
                          } else if (value.attributes.id !== undefined) {
                            value = String(value.attributes.id);
                          } else {
                            value = '';
                          }
                        } else if (value.id !== undefined || value.name !== undefined) {
                          value = value.name || String(value.id) || '';
                        } else if (value.data) {
                          if (typeof value.data === 'object' && value.data !== null) {
                            if (value.data.attributes?.name) {
                              value = value.data.attributes.name;
                            } else if (value.data.name) {
                              value = value.data.name;
                            } else if (value.data.id) {
                              value = String(value.data.id);
                            } else {
                              value = '';
                            }
                          } else {
                            value = String(value.data);
                          }
                        } else {
                          value = String(value);
                        }
                      }
                      
                      return (
                        <TableCell key={columnKey || idx} align={column.align}>
                          {value !== null && value !== undefined ? String(value) : '-'}
                        </TableCell>
                      );
                    })}
                  {(onView || onEdit || onDelete) && (
                    <TableCell align="right">
                      {onView && (
                        <Tooltip title="View">
                          <IconButton size="small" sx={actionButtonSx} onClick={() => onView(normalizedRow)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEdit && (
                        <Tooltip title="Edit">
                          <IconButton size="small" sx={actionButtonSx} onClick={() => onEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDelete && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            sx={actionButtonSx}
                            onClick={() => onDelete(rowId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={enhanced ? { borderTop: '1px solid', borderColor: 'divider' } : undefined}
        />
      )}
    </Paper>
  );
};

export default DataTable;

